import { useCallback, useContext, useEffect, useState } from 'react';
import ProxyLocalStorage, { useProxyLocalStorage } from '../v2/ProxyLocalStorage';
import { ConnectType, MetaData, TomoContext } from '..';
import type {
  Eip155Provider,
} from "@bitget-wallet/omni-connect";
import {
  RequestMethods,
} from "@bitget-wallet/omni-connect/dist/lib/esm/index.mjs.js";

let bitgetProvider: Eip155Provider;

function proxifyBitgetProvider(bitgetProvider: Eip155Provider) {
  const handler: ProxyHandler<Eip155Provider> = {
    get(target: Eip155Provider, property, receiver) {
      switch (property) {
        case 'isConnected': {
          return target.walletInfo.connected;
        }
        default:
          return Reflect.get(target, property, receiver);
      }
    }
  };
  return new Proxy(bitgetProvider, handler)
}

async function getBitGetProvider({ metaData, onConnect, onDisconnect, useEvmChains }: {
  metaData: MetaData;
  onConnect?: () => any;
  onDisconnect?: () => any;
  useEvmChains: number[];
}) {
  if (bitgetProvider) return bitgetProvider
  const bitGetConnector = new (await import('@bitget-wallet/omni-connect')).OmniConnect({
    // const bitGetConnector = new OmniConnect({
    metadata: {
      name: metaData.name,
      iconUrl: metaData.icon,
      url: metaData.url || location.origin, // this is a must, to do
      privacyPolicyUrl: "", // optional
      termsOfUseUrl: "", // optional
    },
    namespace: {
      eip155: {
        chains: useEvmChains.map(id => String(id)),
      },
    },
  });
  const subscription = bitGetConnector.provider.onStatusChange(
    (walletInfo) => {
      console.log("onStatusChange", walletInfo);
      const { id, namespaceKey, event, connected, result } = walletInfo;
      switch (event) {
        case RequestMethods.Connect:
          return onConnect?.();
        case RequestMethods.Disconnect:
          return onDisconnect?.();
        case RequestMethods.SignMessage:
          break;
        case RequestMethods.SignTransaction:
        case RequestMethods.SendTransaction:
          // handle result?.signature, result?.reciept
          break;
        default:
          break;
      }
    },
    (err) => {
      const { code, message } = err;
      console.error(`error stream: code: ${code}, message: ${message}`);
    }
  );
  bitgetProvider = proxifyBitgetProvider(bitGetConnector.provider as Eip155Provider)
  return bitgetProvider
}



interface Option {
  metaData?: MetaData;
  chain?: string;
  theme?: 'dark' | 'light';
}

function useBitget(option?: Option) {
  const { setProviders, useEvmChains, supportedProviders } = useContext(TomoContext)

  const supportedProvider = supportedProviders[0];
  const storage = useProxyLocalStorage()

  function connectEffect(provider: Eip155Provider) {
    setProviders(pre => ({ ...pre, ethereum: provider }))
    const storage = new ProxyLocalStorage()
    const type: ConnectType = 'BITGET'
    storage.set('connect_type', type)
  }

  function disconnectEffect() {
    setProviders(pre => ({ ...pre, ethereum: null }))
    const storage = new ProxyLocalStorage()
    storage.set('connect_type', '')
  }

  const initializeProvider = useCallback(async () => {
    const provider = await getBitGetProvider({
      metaData: option.metaData,
      onConnect: () => {
        connectEffect(provider)
      },
      onDisconnect: () => {
        disconnectEffect()
      },
      useEvmChains
    });
    return provider
  }, [option.metaData, useEvmChains])


  // reconnection check
  useEffect(() => {
    const connectType = storage.get('connect_type') as ConnectType;
    if (connectType === 'BITGET' && supportedProvider === 'EVM') {
      // try reconnect
      initializeProvider().then(async (provider) => {
        // restore
        await provider.restoreConnection()
        setProviders(pre => ({ ...pre, ethereum: provider }))
      })
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supportedProvider, storage])

  const connect = useCallback(async () => {
    const provider = await initializeProvider()
    /** manually set telegram-apps/launch-params */
    const tappsLaunchParams = sessionStorage.getItem('tapps/launchParams')
    sessionStorage.setItem('telegram-apps/launch-params', tappsLaunchParams)
    try {
      const { account, chainId } = await provider.connect();
      console.log("result", account, chainId);
    } catch (error) {
      console.log("error", error);
      throw error
    }
  }, [initializeProvider])

  return {
    connect
  }

}

export default useBitget;
